import React, { useMemo } from 'react';
import { ReactFlow, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';
import EvalNode from 'features/evals3/editor/EvalNode';
import { kindOf } from 'features/evals3/editor/nodeKinds';
import { layoutGraph, edgeList } from 'features/evals3/editor/graphModel';

/**
 * The eval as a proposal would leave it, small and read-only (TRO-435): the
 * step it changes is outlined, steps it adds are marked new, and steps it
 * removes are listed underneath because they are no longer on the graph.
 */

const NODE_TYPES = { eval: EvalNode };

export function proposalMarks(base, proposed, target) {
  const before = (base && base.nodes) || {};
  const after = (proposed && proposed.nodes) || {};
  const added = Object.keys(after).filter((id) => !before[id]);
  const removed = Object.keys(before).filter((id) => !after[id]);
  const changed = Object.keys(after).filter((id) => before[id] && JSON.stringify(before[id]) !== JSON.stringify(after[id]));
  return { added, removed, changed, target };
}

export default function ProposalGraph({ base, proposed, target, height = 260, legend = true }) {
  const marks = useMemo(() => proposalMarks(base, proposed, target), [base, proposed, target]);
  const { nodes, edges } = useMemo(() => {
    if (!proposed || !proposed.nodes) return { nodes: [], edges: [] };
    const positions = layoutGraph(proposed);
    const added = new Set(marks.added);
    const changed = new Set(marks.changed);
    const rfNodes = Object.entries(proposed.nodes).map(([id, node]) => {
      const p = positions.get(id) || { x: 0, y: 0 };
      const outline = id === target ? 'var(--text-primary)' : added.has(id) ? 'var(--brand-primary)' : changed.has(id) ? 'var(--feedback-warning)' : null;
      return {
        id, type: 'eval', position: { x: p.x, y: p.y },
        data: { kind: kindOf(node), node, name: `${node.name || id}${added.has(id) ? ' (new)' : ''}`, isEntry: proposed.entry === id, readOnly: true },
        selected: id === target,
        style: outline ? { outline: `2px solid ${outline}`, outlineOffset: 3, borderRadius: 12 } : undefined,
      };
    });
    const rfEdges = edgeList(proposed).map((e) => ({
      id: `${e.from}>${e.index}>${e.to}`, source: e.from, target: e.to, type: 'smoothstep',
      label: e.label || undefined, labelStyle: { fontSize: 10, fill: 'var(--text-muted)' }, labelBgStyle: { fill: 'var(--surface-base)' },
      style: { stroke: 'var(--border-strong)', strokeWidth: 1.2 },
    }));
    return { nodes: rfNodes, edges: rfEdges };
  }, [proposed, target, marks]);

  return (
    <div data-testid="proposal-graph">
      <div style={{ height }} className="relative overflow-hidden rounded-md border border-[color:var(--border-subtle)]">
        <ReactFlowProvider>
          <ReactFlow
            style={{ background: 'var(--surface-base)' }}
            nodes={nodes} edges={edges} nodeTypes={NODE_TYPES}
            nodesDraggable={false} nodesConnectable={false} elementsSelectable={false}
            proOptions={{ hideAttribution: true }}
            fitView fitViewOptions={{ padding: 0.2, maxZoom: 0.9 }}
            minZoom={0.1} maxZoom={1} panOnDrag zoomOnScroll={false} preventScrolling={false}
          />
        </ReactFlowProvider>
      </div>
      {legend && (
        <div className="mt-1 flex flex-wrap gap-3 text-[10.5px] text-[color:var(--text-muted)]">
          <span>outlined: the step it changes</span>
          {marks.added.length > 0 && <span>new: {marks.added.join(', ')}</span>}
          {marks.changed.filter((id) => id !== target).length > 0 && <span>also changed: {marks.changed.filter((id) => id !== target).join(', ')}</span>}
          {marks.removed.length > 0 && <span className="text-danger">removed: {marks.removed.join(', ')}</span>}
        </div>
      )}
    </div>
  );
}
